// 所有使用者可見的字串集中在這裡（含 hover 說明），元件內不寫死文字。
import type { GenreKey, PlatformKey } from '@dayone/core/dictionaries';

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'] as const;

export const t = {
  app: {
    name: 'Day One',
    tagline: '遊戲發售追蹤',
    skipToList: '跳到遊戲清單',
    attribution: '資料來源：IGDB。遊戲名稱、圖片與商標屬於各自的權利人。',
    privacy: '隱私權政策',
    terms: '服務條款',
  },
  status: {
    label: '顯示',
    upcoming: '未發售',
    released: '已發售',
    hint: '選擇要顯示的遊戲：「未發售」包含日期未定與 TBA；「已發售」預設載入今年與去年，把日期範圍往前拉會載入更早的年份。可以同時勾選。選擇會記在這個瀏覽器。',
    lastOne: '至少要選一項',
  },
  search: {
    placeholder: '搜尋遊戲名稱（中文或英文）',
    label: '搜尋遊戲名稱',
    clear: '清除搜尋',
    hint: '名稱不分大小寫、全形半形；可以打部分名稱，例如「spiderman」也找得到「Spider-Man」。',
  },
  filters: {
    title: '篩選',
    platforms: '平台',
    genres: '類型',
    genresHint: '只顯示屬於任一選取類型的遊戲。類型取自 IGDB。',
    status: '顯示範圍',
    dates: '發售日期',
    done: '完成',
    panel: '篩選條件',
    remove: (label: string) => `移除「${label}」`,
    dateRange: (from: string, to: string) => `${from || '最早'} – ${to || '最晚'}`,
    searchChip: (q: string) => `搜尋：${q}`,
    platformsHint: '只顯示在任一選取平台推出的遊戲。都不選 = 所有平台。選擇會記在這個瀏覽器。',
    dateFrom: '從',
    dateTo: '到',
    dateHint: '以發售日篩選（年-月）。只有年份或季度的遊戲，只要期間與範圍重疊就會顯示；TBA 只在沒設定範圍時顯示。',
    sort: '排序',
    sortHint: '依發售日排序時會依月份分組；日期不明確的遊戲排在所屬期間的最後，TBA 排在最後。',
    sortOptions: {
      date: '發售日（近 → 遠）',
      'date-desc': '發售日（新 → 舊）',
      hype: '期待度',
      name: '名稱',
    },
    view: '顯示方式',
    viewScroll: '連續捲動',
    viewPages: '分頁',
    viewHint: '連續捲動只繪製看得到的範圍，清單再長也不卡；分頁適合一頁一頁瀏覽或分享某一頁的網址。',
    pageSize: '每頁',
    reset: '清除全部篩選',
    active: (n: number) => `${n} 個篩選條件`,
  },
  platforms: {
    pc: 'PC',
    ps5: 'PS5',
    ps4: 'PS4',
    xsx: 'Xbox Series',
    xone: 'Xbox One',
    switch: 'Switch',
    switch2: 'Switch 2',
    mac: 'Mac',
    linux: 'Linux',
    ios: 'iOS',
    android: 'Android',
  } satisfies Record<PlatformKey, string>,
  genres: {
    rpg: '角色扮演',
    adventure: '冒險',
    shooter: '射擊',
    fighting: '格鬥',
    platform: '平台跳躍',
    puzzle: '解謎',
    racing: '競速',
    rts: '即時戰略',
    simulator: '模擬',
    sport: '運動',
    strategy: '策略',
    tbs: '回合制策略',
    tactical: '戰術',
    'hack-slash': '砍殺',
    indie: '獨立',
    arcade: '街機',
    music: '音樂',
    'point-click': '點擊冒險',
    'visual-novel': '視覺小說',
    'card-board': '卡牌桌遊',
    moba: 'MOBA',
    quiz: '益智問答',
    pinball: '彈珠台',
  } satisfies Record<GenreKey, string>,
  date: {
    weekday: (i: number) => WEEKDAYS[i] ?? '',
    day: (y: number, m: number, d: number, w: string, showYear: boolean) =>
      showYear ? `${y}/${m}/${d}（${w}）` : `${m}/${d}（${w}）`,
    month: (y: number, m: number) => `${y} 年 ${m} 月`,
    quarter: (y: number, q: number) => `${y} Q${q}`,
    year: (y: number) => `${y} 年`,
    tba: '未定',
    precision: {
      day: '確定日期',
      month: '只公布月份',
      quarter: '只公布季度',
      year: '只公布年份',
      tba: '尚未公布',
    },
    precisionHint: {
      day: '官方已公布確切發售日。',
      month: '只公布到月份，實際日期可能在該月任何一天。定檔後日期會更新。',
      quarter: '只公布到季度，實際日期可能在該季任何一天。定檔後日期會更新。',
      year: '只公布年份。定檔後日期會更新。',
      tba: '尚未公布任何發售時間。',
    },
  },
  group: {
    count: (n: number) => `${n} 款`,
    week: '本週',
    nextWeek: '下週',
    range: (a: string, b: string) => `${a} – ${b}`,
    monthLabel: (m: number) => `${m} 月`,
    monthSub: (y: number, exact: number, fuzzy: number) =>
      [String(y), exact ? `${exact} 款確定` : '', fuzzy ? `${fuzzy} 款只到月份` : ''].filter(Boolean).join(' · '),
    quarterLabel: (y: number, q: number) => `${y} 第 ${q} 季`,
    yearLabel: '年內',
    undated: '日期未定',
    tbaLabel: '尚未公布',
    tbaSub: '沒有任何發售時間',
    overdueLabel: '可能過期',
    overdueSub: '公布的期間早已過去，可能已延期或資料未更新',
    /** 完整名稱（螢幕閱讀器、測試） */
    full: {
      week: (range: string) => `本週（${range}）`,
      nextWeek: (range: string) => `下週（${range}）`,
      month: (y: number, m: number) => `${y} 年 ${m} 月`,
      quarter: (y: number, q: number) => `${y} 年 Q${q}`,
      year: (y: number) => `${y} 年`,
      tba: '未定（TBA）',
      overdue: '可能過期（公布的期間早已過去）',
    },
  },
  countdown: {
    today: '今天發售',
    tomorrow: '明天發售',
    days: '天後發售',
    releaseOn: (date: string) => `${date} 發售`,
  },
  hero: {
    week: '本週發售',
    hype: '最受期待',
    hypeNote: '依 IGDB 期待人數',
    label: '精選遊戲',
    slide: (i: number, n: number) => `第 ${i} 款，共 ${n} 款`,
    goTo: (name: string) => `切換到 ${name}`,
    details: '查看詳情',
    pause: '暫停輪播',
    play: '繼續輪播',
  },
  card: {
    released: '已發售',
    earlyAccess: '搶先體驗',
    stale: '資料可能過期',
    earlyAccessNow: '搶先體驗中',
    earlyAccessUpcomingHint: '目前可以玩搶先體驗版；這裡的日期是正式版的發售日。',
    staleHint: '公布的發售期間已經過去，卻還沒有發售紀錄：可能已延期，或資料來源尚未更新。',
    conflict: '來源不一致',
    conflictHint: '不同資料來源公布的發售日不同，目前顯示優先序最高的來源。詳情頁會列出各來源的說法。',
    platformDates: '部分平台日期不同',
    platformDatesHint: (lines: string[]) => `各平台發售日：${lines.join('、')}`,
    hype: '期待度',
    hypeHint: (pct: number, raw: number) =>
      `在目前清單的未發售遊戲中，期待人數勝過 ${pct}% 的遊戲（IGDB 期待數 ${raw}）。`,
    noHype: '尚無期待度資料',
    platformsHint: (names: string) => `推出平台：${names}`,
    coverAlt: (name: string) => `${name} 封面`,
    /** 日期牌：確定日期的月份、今天 */
    chipMonth: (m: number) => `${m}月`,
    chipToday: '今天',
    chipUndated: '未定',
  },
  list: {
    loading: '載入遊戲清單中…',
    summary: (shown: number, total: number) => `顯示 ${shown} 款（共 ${total} 款）`,
    empty: '沒有符合條件的遊戲',
    emptyHint: '試試放寬以下條件：',
    clearFilter: (label: string) => `清除「${label}」`,
    clearAll: '清除全部條件',
    backToTop: '回到頂端',
  },
  pagination: {
    label: '分頁',
    prev: '上一頁',
    next: '下一頁',
    page: (p: number, total: number) => `第 ${p} / ${total} 頁`,
  },
  error: {
    title: '資料載入失敗',
    network: '無法連線到伺服器，請確認網路後重試。',
    notFound: '找不到資料檔，可能正在更新，請稍後重試。',
    schema: '網站已更新，請重新整理頁面。',
    unknown: '發生未預期的錯誤。',
    retry: '重試',
    reload: '重新整理',
  },
  freshness: {
    updated: (ago: string) => `資料更新於 ${ago}`,
    hintTitle: '各資料來源最後一次成功更新',
    never: '尚未成功更新',
    degraded: '更新異常',
    degradedBanner: '部分資料來源暫時無法更新，顯示的可能不是最新資料。',
    fixture: '目前顯示的是測試資料（虛構遊戲），不是真實發售資訊。',
    ago: {
      justNow: '剛剛',
      minutes: (n: number) => `${n} 分鐘前`,
      hours: (n: number) => `${n} 小時前`,
      days: (n: number) => `${n} 天前`,
    },
  },
  detail: {
    open: (name: string) => `查看「${name}」詳情`,
    close: '關閉',
    loading: '載入詳情中…',
    notFound: '找不到這款遊戲，可能已被移除或合併到其他項目。',
    releaseDate: '發售日',
    platformDates: '各平台發售日',
    platformDatesHint: '同一款遊戲在不同平台的發售日可能不同，這裡列出各平台最早的日期。',
    platform: '平台',
    conflicts: '來源說法不一致',
    conflictsHint:
      '不同資料來源公布的發售日不同。清單上顯示的是優先序最高的來源（官方商店 > IGDB > 其他）；定檔後通常會一致。',
    summary: '簡介',
    summaryNote: '簡介取自 IGDB（英文）。',
    trailers: '預告片',
    playTrailer: (name: string) => `播放預告片：${name}`,
    trailerHint: '先顯示縮圖，點擊後才載入 YouTube 播放器，避免還沒要看就消耗流量與追蹤。',
    screenshots: '截圖',
    screenshotAlt: (name: string, i: number) => `${name} 截圖 ${i}`,
    genres: '類型',
    links: '官方與商店連結',
    newTab: '（在新分頁開啟）',
    tabs: {
      label: '詳情分頁',
      overview: '總覽',
      media: '媒體',
      dates: '發售日',
      links: '連結',
      sources: '資料來源',
    },
    watchTrailer: '看預告片',
    goTo: (name: string) => `前往 ${name}`,
    facts: '發售資訊',
    noSummary: '尚無簡介。',
    lightbox: (name: string, i: number, n: number) => `${name} 截圖 ${i} / ${n}`,
    prevShot: '上一張',
    nextShot: '下一張',
    openShot: (i: number) => `放大第 ${i} 張截圖`,
    dragToClose: '向下拖曳關閉',
    sources: '資料來源',
    sourcesHint: '每個欄位採用的資料來源與該筆資料最後一次變動的時間。多個來源不一致時，依優先序採用。',
    sourceLine: (field: string, source: string, ago: string) => `${field}：${source}（${ago}變動）`,
    fields: {
      identity: '名稱與簡介',
      release: '發售日',
      classification: '平台與類型',
      media: '圖片與影片',
      popularity: '期待度',
    } as Record<string, string>,
  },
  /** Google 行事曆同步（GDO-24～GDO-27）；「月曆」檢視另見 calendar */
  gcal: {
    title: 'Google 行事曆',
    entry: 'Google 行事曆',
    entrySignedOut: '連結 Google 行事曆',
    entryReauth: '需要重新連結',
    entryUpdate: '更新',
    entryUpdateHint: (n: number) =>
      `有 ${n} 款遊戲的發售日變動了，行事曆裡還是舊的日期。按一下一次更新所有日期變動的遊戲（你寫在事件裡的筆記會保留）。`,
    entryUpdating: (done: number, total: number) => `${done}/${total}`,
    connect: '連結 Google 行事曆',
    reconnect: '重新連結 Google',
    logout: '登出並撤銷授權',
    close: '關閉',
    intro:
      '連結後可以把遊戲加入行事曆：建立全天活動，說明附上各平台發售日、預告片與商店連結；日期只公布到年份、季或月份的遊戲以預計的期間加入。發售日變動時可以一鍵更新。已經自己建立過的事件，也可以綁定到遊戲。',
    permissions:
      '只會要求兩個權限：讀寫行事曆中的事件、讀取你的行事曆清單。登入狀態只存在這個瀏覽器，不會存到伺服器的資料庫。',
    notConfigured: '這個網站還沒有設定 Google 行事曆（需要管理者設定 Google OAuth）。',
    reauth: 'Google 的授權已經失效或被撤銷，請重新連結。其他瀏覽功能不受影響。',
    account: (email: string) => `已連結：${email}`,
    calendar: '同步到',
    chooseCalendar: '選擇要同步的行事曆',
    chooseHint: '找不到名為「Exciting Event」的行事曆，請選擇要把遊戲加到哪個行事曆。選擇會記在這個瀏覽器。',
    loading: '讀取行事曆…',
    refresh: '重新讀取',
    summary: (synced: number, drift: number, orphan: number) =>
      [`${synced} 款已加入`, drift ? `${drift} 款改期` : '', orphan ? `${orphan} 款已取消或日期未公布` : '']
        .filter(Boolean)
        .join(' · '),
    driftTitle: '發售日已變動',
    driftBanner: (n: number) => `${n} 款遊戲的發售日改了，行事曆還是舊的日期`,
    updateAll: '全部更新',
    updating: (done: number, total: number) => `更新中 ${done} / ${total}`,
    orphanTitle: '已取消或日期未公布',
    suggestionsTitle: '這些是你行事曆裡的遊戲嗎？',
    suggestionsHint:
      '依事件標題與日期比對（忽略大小寫、全半形、重製版等後綴）。綁定後會在事件說明加上遊戲資訊，標題、日期、提醒與顏色都不變。',
    isThis: (name: string) => `這是 ${name} 嗎？`,
    bind: '綁定',
    notThis: '不是',
    otherEvents: '其他事件',
    otherHint: '行事曆中還沒綁定遊戲的事件。可以搜尋遊戲後綁定。',
    findGame: '搜尋要綁定的遊戲',
    noEvents: '沒有其他事件',
    // 卡片與詳情
    add: '加入行事曆',
    added: '已加入行事曆',
    open: '在 Google 日曆開啟',
    updateEvent: '更新事件',
    updateDesc: '更新說明',
    unbind: '解除綁定',
    removeEvent: '從行事曆移除',
    bindMine: '綁定到我的事件',
    bindPick: '選擇要綁定的事件（發售日前後 90 天）',
    noNearby: '發售日前後 90 天內沒有其他事件',
    needConnect: '連結後可用',
    needConnectHint: '連結 Google 行事曆後，就能把遊戲加入行事曆或綁定你自己的事件。',
    notDay: '日期未公布',
    tbaHint: '發售日還沒公布（連年份都未定），公布後才能加入行事曆。',
    periodPassed: '預計期間已過',
    periodPassedHint: (when: string) =>
      `原本預計「${when}」發售，但這段期間已經過了、還沒有新的日期。公布新的日期後才能加入行事曆。`,
    span: (start: string, end: string) => `${start}～${end}`,
    fuzzyAddHint: (when: string, span: string) =>
      `發售日只公布到「${when}」，會加入一個 ${span} 的全天活動，說明中寫明尚未公布確切日期。確定日期後，側欄的「更新」可以把它改成發售當天。`,
    fuzzyNote: '日期還沒確定：先以預計的期間加入，確定日期後可以一鍵更新成發售當天。',
    syncedHint: (date: string, bound: boolean, range = false) =>
      bound
        ? `已綁定你自己的事件（${date}）。說明中加上了遊戲資訊。`
        : range
          ? `行事曆中已有這款遊戲（${date}，日期確定後可以更新成發售當天）。`
          : `行事曆中已有這款遊戲（${date}）。`,
    drift: (from: string, to: string) => `發售日已從 ${from} 改為 ${to}`,
    driftHint: '行事曆中的日期是舊的。按「更新事件」把日期改成目前的發售日，說明也會一起更新；你寫的筆記會保留。',
    orphan: '遊戲已取消或日期未公布',
    orphanHint: '行事曆中還有這款遊戲的事件，但它已經取消或發售日變回未公布。可以移除或保留。',
    descStale: '說明有新資料',
    descStaleHint: '評分、預告片或連結有更新。按「更新說明」重新產生事件說明（日期不變、你的筆記保留）。',
    sameDay: (n: number) => `同日還有 ${n} 款`,
    sameDayHint: '同一天還有其他你加入行事曆的遊戲（只是提醒，不是錯誤）。',
    platformNote: (names: string) => `事件日期以你選的平台（${names}）中最早的日期為準。`,
    badge: { synced: '已加入行事曆', drift: '行事曆日期已過時', orphan: '行事曆中的遊戲已取消或日期未公布' },
    messages: {
      connected: '已連結 Google 行事曆。',
      updated_all: '已全部更新。',
      denied: '你取消了 Google 授權，沒有連結行事曆。',
      scope: '沒有取得讀寫行事曆的權限。請重新連結，並在 Google 的畫面勾選行事曆權限。',
      invalid_state: '登入流程已過期或不完整，請重新連結。',
      not_configured: '這個網站還沒有設定 Google 行事曆。',
      signed_out: '尚未連結 Google 行事曆。',
      reauth_required: 'Google 授權已失效，請重新連結。',
      forbidden: '沒有這個行事曆的寫入權限，請選擇其他行事曆。',
      not_found: '找不到這個事件，可能已經被刪除。請重新讀取行事曆。',
      calendar_not_found: '找不到選擇的行事曆，請重新選擇。',
      not_day: '發售日還沒公布，暫時不能加入。',
      period_passed: '預計的發售期間已經過了，等公布新的日期後再加入。',
      stale_snapshot: '資料已經更新，請重新整理頁面後再試。',
      bound_event: '這是你自己建立的事件，只能解除綁定。',
      google_unavailable: '暫時無法連線 Google，請稍後再試。',
      network: '沒有網路連線，行事曆暫時無法使用。',
      unknown: '行事曆操作失敗，請稍後再試。',
    } as Record<string, string>,
  },
  editions: {
    count: (n: number) => `${n} 個版本`,
    standard: '標準版',
    hint: (lines: string[]) => `這款遊戲有 ${lines.length} 個版本：${lines.join('、')}。詳情頁可以比較各版本。`,
    early: (label: string, date: string) => `${label} ${date} 搶先玩`,
    earlyHint: (label: string, days: number) => `購買${label}可以提前 ${days} 天遊玩（正式發售日不變）。`,
    earlyDays: (n: number) => `提前 ${n} 天`,
    section: '版本',
    sectionHint:
      '同一款遊戲的不同版本（例如加上額外內容或提前遊玩的豪華版）合併在這裡，不再各自列成一款遊戲。重製、強化版與新平台版本仍然各自列出。',
    buy: (label: string) => `購買${label}`,
    /** 常見版本名稱的中文（比對時不分大小寫） */
    names: {
      'standard edition': '標準版',
      'deluxe edition': '豪華版',
      'digital deluxe edition': '數位豪華版',
      'super deluxe edition': '超級豪華版',
      'ultimate edition': '終極版',
      'gold edition': '黃金版',
      'premium edition': '高級版',
      "collector's edition": '典藏版',
      'collectors edition': '典藏版',
      'complete edition': '完整版',
      'special edition': '特別版',
      'limited edition': '限定版',
      'launch edition': '首發版',
      'legendary edition': '傳奇版',
      'day one edition': '首日版',
    } as Record<string, string>,
  },
  linkKinds: {
    official: '官方網站',
    steam: 'Steam',
    epic: 'Epic Games Store',
    gog: 'GOG',
    itch: 'itch.io',
    nintendo: 'Nintendo eShop',
    playstation: 'PlayStation Store',
    xbox: 'Xbox Store',
    wikipedia: 'Wikipedia',
    youtube: 'YouTube',
    twitter: 'X（Twitter）',
    discord: 'Discord',
    reddit: 'Reddit',
    igdb: 'IGDB',
  } as Record<string, string>,
  hint: {
    trigger: '說明',
  },
  tour: {
    label: '使用導覽',
    replay: '使用導覽',
    step: (i: number, n: number) => `${i} / ${n}`,
    next: '下一步',
    prev: '上一步',
    skip: '略過',
    done: '開始使用',
    steps: {
      timeline: {
        title: '依時間排好的發售表',
        body: '遊戲依發售日分段：本週、下週、各月份，日期只公布到月份、季或年的放在該期間最後。段落標題會跟著捲動。',
      },
      filters: {
        title: '只看你在意的',
        body: '按「篩選」選平台、類型、發售日期與排序；生效的條件會顯示在上方，可以一個一個移除。按 Ctrl K 可以直接搜尋遊戲。',
      },
      detail: {
        title: '點開看詳情',
        body: '點任一款遊戲看預告片、截圖、各平台發售日與商店連結。日期牌是虛線框時，代表只公布到月份或季。',
      },
      install: {
        title: '裝成 App，離線也能看',
        body: '安裝後可以從桌面或主畫面直接開啟，沒有網路時也能看上次看過的發售表。',
        phoneBody:
          '在「篩選」面板底部可以安裝 Day One（iPhone 請用 Safari 的「加入主畫面」），沒有網路時也能看上次看過的發售表。',
      },
    },
  },
  pwa: {
    offline: (time: string) => `離線中 · 顯示 ${time} 的資料`,
    offlineNoData: '離線中 · 目前沒有可顯示的資料',
    offlineTitle: '目前沒有網路',
    offlineBody: '這台裝置還沒有存下發售表。連上網路後會自動載入，之後沒有網路也能看。',
    offlineHint: '沒有網路時會顯示上次看過的資料（清單、看過的詳情、封面）。恢復連線後會自動更新。',
    updateReady: '有新版本',
    updateAction: '重新整理',
    install: '安裝 Day One',
    installDesc: '離線也能看發售表',
    iosInstall: '加入主畫面',
    iosInstallHint: '在 Safari 按下方的「分享」按鈕，選「加入主畫面」，就能像 App 一樣開啟 Day One。',
  },
  palette: {
    label: '搜尋遊戲',
    trigger: '搜尋遊戲（⌘K）',
    placeholder: '輸入遊戲名稱（中文或英文）',
    shortcut: (mac: boolean) => (mac ? '⌘K' : 'Ctrl K'),
    results: '搜尋結果',
    suggestions: '最受期待',
    none: '找不到符合的遊戲',
    showAll: (q: string) => `在清單中顯示所有符合「${q}」的遊戲`,
    help: '↑↓ 選擇 · Enter 開啟 · Esc 關閉',
    hint: '名稱不分大小寫、全形半形；可以打部分名稱，例如「spiderman」也找得到「Spider-Man」。',
  },
  calendar: {
    title: (y: number, m: number) => `${y} 年 ${m} 月`,
    summary: (dated: number, undated: number) =>
      [`${dated} 款確定日期`, undated ? `${undated} 款只到月份` : ''].filter(Boolean).join(' · '),
    prev: '上個月',
    next: '下個月',
    thisMonth: '本月',
    weekdays: ['日', '一', '二', '三', '四', '五', '六'] as const,
    day: (m: number, d: number, w: string, n: number) => `${m} 月 ${d} 日（${w}），${n ? `${n} 款` : '沒有遊戲'}`,
    more: (n: number) => `+${n}`,
    dayTitle: (m: number, d: number, w: string) => `${m}/${d}（${w}）`,
    undatedTitle: (m: number) => `${m} 月 · 日期未定`,
    undatedHint: '只公布到月份的遊戲，實際日期可能在這個月的任何一天。',
    empty: '這個月沒有符合條件的遊戲',
    today: '今天',
    weeks: '週檢視，左右滑動切換週',
  },
  layout: {
    label: '版面',
    grid: '海報牆',
    list: '清單',
  },
  nav: {
    label: '主選單',
    upcoming: '即將發售',
    calendar: '月曆',
    released: '已發售',
    search: '搜尋',
    upcomingCount: (n: number) => `${n} 款未發售`,
    quickPlatforms: '常用平台',
    display: '顯示設定',
  },
  theme: {
    label: '外觀',
    options: {
      dark: '深色',
      light: '淺色',
      system: '跟隨系統',
    },
  },
} as const;
